import React from 'react';
import styled from 'styled-components';
import DoughnutGauge from '@/areas/dashboard/components/DoughnutGauge';
import { useWelcomeTourData } from '../useWelcomeTourData';
import SlideLoading from './SlideLoading';
import { HeroBody, HeroHeadline, HeroLabel, SlideContainer, HeroBanner, BannerDecoClip, EmptyState } from './slideStyles';
import { useSelector } from 'react-redux';
import { getLeadershipTeams, getSelectedAssessmentPeriod } from '@/common/selectors/auth';
import userContextSlice from '@/common/slices/userContextSlice';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import '@/areas/dashboard/dashboard.css';


const ContentContainer = styled.div`
  width: min(100%, 1140px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeaderPanel = styled.div`
  width: 100%;
  padding: 0.75rem 1.75rem 0rem;
  box-sizing: border-box;

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 0.5rem 0.65rem 0;
  }
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 900px) {
    display: flex;
    flex-direction: column;
  }
`;

const HeroIntroText = styled.div`
  min-width: 0;
`;

const AISummaryText = styled.p`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(14px, 2vh, 16px);
  font-weight: 400;
  color: #475569;
  line-height: 1.95;
  min-width: 0;
`;

const AISummaryBox = styled.div`
  padding: 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  border-top: 1px solid rgba(148, 163, 184, 0.18);

  @media (max-width: 900px) {
    width: 100%;
    padding: 1.25rem 1.5rem;
  }

  @media (max-width: 600px) and (orientation: portrait) {
    padding: 1rem 0.75rem;
  }
`;

const ScoreBadge = styled.div`
  position: relative;
  width: fit-content;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ScoreLabel = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const TrendBadgeOverlay = styled.div`
  position: absolute;
  bottom: -0.65rem;
  right: -1.5rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #111827;
`;

const HeroGaugeBox = styled.div`
  flex-shrink: 0;
  min-width: 220px;
  width: 220px;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1rem 1.5rem;

  @media (max-width: 900px) {
    display: none;
  }
`;
const ChangeNote = styled.p`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(14px, 2vh, 16px);
  font-weight: 400;
  color: #475569;
  line-height: 1.95;
`;

const Emphasis = styled.span`
  font-weight: 600;
  color: #111827;
`;

const ThemeStatusIcon = styled.span<{ $color: string }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${props => props.$color};
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  font-weight: 700;
  flex-shrink: 0;
  margin-left: 0.3rem;
`;

const PanelLabel = styled.div<{ $color: string }>`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.5rem;
  align-items: center;
  padding-bottom: 0.25rem;
  line-height: 1.3;
`;

const PanelTitle = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(16px, 2vh, 20px);
  font-weight: 700;
  color: #1E3A8A;
`;

const BulletList = styled.div`
  display: flex;
  flex-direction: column;
  gap: .75rem;
`;

const BulletItem = styled.div`
  display: flex;
  gap: .5rem;
  align-items: flex-start;
  margin-left: .75rem;
`;

const BulletIcon = styled.span<{ $color: string }>`
  color: ${props => props.$color};
  font-size: clamp(11px, 1.5vh, 16px);
  margin-top: 8px;
`;

const ExpandedSlideContainer = styled(SlideContainer)`
  height: 100%;
  flex: 1;
  min-height: 0; 
`;

const Slide2AssessmentHighlights: React.FC = () => {
    const [isShortScreen, setIsShortScreen] = React.useState(false);
    const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);

    React.useEffect(() => {
        const mediaQuery = window.matchMedia('(max-height: 760px)');
        const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
            setIsShortScreen(event.matches);
        };

        handleChange(mediaQuery);
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const { overallScore, previousScore, differential, analytics, aiSummary, isLoading } =
        useWelcomeTourData();
    const leadershipTeams = useSelector(getLeadershipTeams);
    const selectedTeamId = useSelector(
        userContextSlice.selectors.getSelectedTeamId as any
    ) as number | undefined;
    const teamName = leadershipTeams?.find((team) => team.id === selectedTeamId)?.name;

    const heroGaugePanel = overallScore != null ? (
        <HeroGaugeBox>
            <ScoreLabel>Culture Score</ScoreLabel>
            <ScoreBadge>
                <DoughnutGauge max={10} complete={overallScore} precision={1} size={"smallPlus"} colorize title="" />
                {differential !== null && previousScore !== null && (
                    <TrendBadgeOverlay>
                        {differential > 0 ? (
                            <TrendingUp size={14} />
                        ) : differential < 0 ? (
                            <TrendingDown size={14} />
                        ) : (
                            <Minus size={14} />
                        )}
                        {differential > 0 ? `+${differential.toFixed(1)}` : differential.toFixed(1)}
                    </TrendBadgeOverlay>
                )}
            </ScoreBadge>
        </HeroGaugeBox>
    ) : null;

    const summaryContent = aiSummary && overallScore != null ? (
        <AISummaryBox>
            <BulletList>
                <PanelLabel $color="#3d61d6">
                    <ThemeStatusIcon $color="#3d61d6">
                        {'='}
                    </ThemeStatusIcon>
                    <PanelTitle>Team Summary</PanelTitle>
                </PanelLabel>

                <BulletItem>
                    <BulletIcon $color="#2563EB">•</BulletIcon>
                    <ChangeNote>
                        Your team scored <Emphasis>{overallScore.toFixed(1)}</Emphasis> out of <Emphasis>10</Emphasis> on the culture assessment.
                        {differential !== null && previousScore !== null && (
                            differential === 0 ? (
                                <> This score has <Emphasis>stayed the same</Emphasis> since the last assessment.</>
                            ) : (
                                <> This score has <Emphasis>{differential > 0 ? 'improved' : 'declined'}</Emphasis> by <Emphasis>{Math.abs(differential).toFixed(1)} points</Emphasis> since the last assessment.</>
                            )
                        )}
                    </ChangeNote>
                </BulletItem>
                <BulletItem>
                    <BulletIcon $color="#2563EB">•</BulletIcon>
                    <AISummaryText>{aiSummary}</AISummaryText>
                </BulletItem>
            </BulletList>
        </AISummaryBox>
    ) : null;

    return (
        <ExpandedSlideContainer>
            <HeroBanner>
                <BannerDecoClip />
                <ContentContainer>
                    <HeaderPanel>
                        <HeaderGrid>
                            <HeroIntroText>
                                <HeroLabel>Assessment highlights - {selectedAssessmentPeriod?.name || 'Current Assessment Period'}</HeroLabel>
                                <HeroHeadline>
                                    {teamName
                                        ? `Key takeaways from ${teamName}`
                                        : 'Your team has completed their culture assessment'}
                                </HeroHeadline>
                                <HeroBody>
                                    By combining culture scores with team feedback, these insights reveal what's driving your results and where your efforts can have the greatest impact.
                                </HeroBody>
                            </HeroIntroText>
                            {heroGaugePanel}
                        </HeaderGrid>
                    </HeaderPanel>
                    {isLoading ? (
                        <SlideLoading message="Loading insights..." />
                    ) : summaryContent ? (
                        summaryContent
                    ) : (
                        <EmptyState>Summary information will appear once your assessment is processed.</EmptyState>
                    )}
                </ContentContainer>
            </HeroBanner>
        </ExpandedSlideContainer>
    );
};

export default Slide2AssessmentHighlights;
